import {
  DragOverlay,
  defaultDropAnimationSideEffects,
  useDraggable,
} from "@dnd-kit/core";
import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "@jugl-web/ui-components/cross-platform";
import { TaskCardPill } from "@jugl-web/ui-components/cross-platform/tasks/TaskCardPill";
import { TaskLabelContainer } from "@jugl-web/ui-components/cross-platform/tasks/TaskLabelGroup";
import { cx, useTranslations, useUniqueId } from "@jugl-web/utils";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import format from "date-fns/format";
import { FC, HTMLAttributes } from "react";
import { createPortal } from "react-dom";
import Highlighter from "react-highlight-words";
import { TaskCardDraggableData } from "../../types";
import { shortenTaskTitle } from "../../utils";
import { ReactComponent as BellIcon } from "./assets/bell.svg";
import { ReactComponent as CheckBlankIcon } from "./assets/check-blank.svg";
import { ReactComponent as ClockIcon } from "./assets/clock.svg";

interface TaskCardChecklist {
  completedItemsCount: number;
  totalItemsCount: number;
  isOverdue: boolean;
}

interface TaskCardTimeSpent {
  hours: number;
  minutes: number;
}

export interface TaskCardField {
  id: string;
  text: string;
  textColor: string;
  backgroundColor: string;
}

export interface TaskCardProps extends HTMLAttributes<HTMLDivElement> {
  taskId: string;
  isUnread: boolean;
  title: string;
  dueDate: Date | null;
  isOverdue: boolean;
  isCompleted: boolean;
  checklist: TaskCardChecklist;
  timeSpent: TaskCardTimeSpent;
  fields: TaskCardField[];
  highlightedText?: string;
  draggable?: boolean;
  columnId?: string;
}

export const TaskCard: FC<TaskCardProps> = (props) => {
  const {
    taskId,
    isUnread,
    title,
    dueDate,
    isOverdue,
    isCompleted,
    checklist,
    timeSpent,
    fields,
    highlightedText,
    className,
    style,
    draggable,
    columnId,
    onClick,
    ...divProps
  } = props;

  const { t } = useTranslations();
  const { dateLocale } = useLanguage();
  const uniqueId = useUniqueId();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${taskId}-${uniqueId}`,
    disabled: !draggable,
    data: { type: "task", taskId, columnId } satisfies TaskCardDraggableData,
  });

  const hasTimeSpent = timeSpent.hours > 0 || timeSpent.minutes > 0;
  const shortenedTitle = shortenTaskTitle(title);

  return (
    <>
      {isDragging &&
        createPortal(
          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: "1",
                  },
                },
              }),
            }}
          >
            <TaskCard
              {...props}
              draggable={false}
              className="border-primary-600 border-px rotate-2 border-dashed opacity-80"
            />
          </DragOverlay>,
          document.body
        )}
      <InteractiveContainer
        ref={setNodeRef}
        className={cx(
          "relative touch-pan-y overflow-hidden rounded-2xl bg-white px-4 pb-4 transition-shadow",
          {
            "shadow-task-card hover:shadow-task-card-hovered": !isDragging,
          },
          isUnread ? "pt-7" : "pt-4",
          className
        )}
        style={{ WebkitTapHighlightColor: "transparent", ...style }}
        onClick={onClick as InteractiveContainerProps["onClick"]}
        {...listeners}
        {...attributes}
        {...divProps}
      >
        {isDragging && <div className="absolute inset-0 z-20 bg-[#E7E9EA]" />}
        {isUnread && (
          <TaskLabelContainer className="bg-gradients-danger absolute right-0 top-0 rounded-bl-xl px-2">
            <BellIcon />
          </TaskLabelContainer>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-dark break-words text-sm font-medium leading-3 tracking-[0.14px]">
            {highlightedText ? (
              <Highlighter
                autoEscape
                highlightClassName="text-primary bg-primary-50"
                highlightTag="span"
                textToHighlight={shortenedTitle}
                searchWords={[highlightedText]}
              />
            ) : (
              shortenedTitle
            )}
          </span>
          {dueDate && (
            <span
              className={cx(
                "leading-2 self-start text-xs tracking-[0.12px]",
                isOverdue
                  ? "text-tertiary-700 bg-tertiary-50 rounded-md py-1 px-2 font-medium"
                  : "text-[#828282]"
              )}
            >
              {format(dueDate, "MMMM dd, yyyy", { locale: dateLocale })}
            </span>
          )}
        </div>
        {(checklist.totalItemsCount > 0 || hasTimeSpent) && (
          <div className="my-2 flex items-center gap-2 py-2">
            {checklist.totalItemsCount > 0 && (
              <div className="flex items-center gap-2">
                <div
                  className={cx(
                    "flex items-center gap-2 font-medium",
                    checklist.isOverdue
                      ? "text-tertiary-700 bg-tertiary-50 rounded-md py-1 px-2"
                      : "text-grey"
                  )}
                >
                  <CheckBlankIcon
                    className={cx(
                      checklist.isOverdue
                        ? "text-gradients-danger"
                        : "text-grey-600"
                    )}
                  />
                  <span className="text-xs leading-[14px]">
                    {checklist.completedItemsCount}/{checklist.totalItemsCount}
                  </span>
                </div>
                <div className="bg-dark-100 relative h-2.5 w-20 overflow-hidden rounded-lg">
                  <div
                    className="bg-grey-400 absolute inset-y-0 left-0 rounded-lg"
                    style={{
                      width: `${
                        (checklist.totalItemsCount === 0
                          ? 0
                          : checklist.completedItemsCount /
                            checklist.totalItemsCount) * 100
                      }px`,
                    }}
                  />
                </div>
              </div>
            )}
            {checklist.totalItemsCount > 0 && hasTimeSpent && (
              <div className="bg-grey-200 h-6 w-px" />
            )}
            {hasTimeSpent && (
              <div className="flex items-center gap-1.5">
                <ClockIcon />
                <span className="text-grey text-xs font-medium">
                  {t(
                    {
                      id: "tasks-page.time-spent-on-task-duration",
                      defaultMessage: "{hours}h : {minutes}min",
                    },
                    { hours: timeSpent.hours, minutes: timeSpent.minutes }
                  )}
                </span>
              </div>
            )}
          </div>
        )}
        {fields.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {fields.map((field) => (
              <TaskCardPill
                key={field.id}
                label={field.text}
                backgroundColor={field.backgroundColor}
                textColor={field.textColor}
              />
            ))}
          </div>
        )}
      </InteractiveContainer>
    </>
  );
};
